import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy as CloneIcon, Pencil as EditIcon, Trash2 as DeleteIcon } from 'lucide-react';
import { FormSection, InlineErrorPanel } from '@coordio/ui/layout';
import { Chip } from '@coordio/ui';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { agentService } from '../services/agentService';
import { useNotificationStore } from '../store/notificationStore';
import { ParameterList } from '../components/agents/ParameterList';
import { AgentRunBench } from '../components/agents/AgentRunBench';
import { AgentMarkdownFileList } from '../components/agents/AgentMarkdownFileList';
import { AgentKnowledgeFileList } from '../components/agents/AgentKnowledgeFileList';
import { AgentDefinitionFunctionList } from '../components/agents/AgentDefinitionFunctionList';
import { AgentMcpServerList } from '../components/agents/AgentMcpServerList';
import { AgentSkillList } from '../components/agents/AgentSkillList';
import { AgentContextWindowPanel } from '../components/agents/AgentContextWindowPanel';
import { AttachedAgentsPanel } from '../components/orchestrator/AttachedAgentsPanel';
import { CodeEditor } from '../components/common/CodeEditor';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import type {
  AgentDefinitionDto,
  AgentImageContextMode,
  AgentSpeechConfig,
  CreateAgentParameterRequest,
  UpdateAgentParameterRequest,
} from '../types/agent';

import './AgentDetailPage.scss';

// ─── Image context mode helpers ──────────────────────────────────────────────

/** Labels keyed by API enum ordinal — JSON may serialize ImageContextMode as a number. */
const IMAGE_CONTEXT_MODE_LABELS = [
  'OCR / layout text',
  'AI image description',
  'Ignore attachments',
  'Vision only (no preprocessing)',
] as const;

const IMAGE_CONTEXT_MODE_BY_NAME: Record<string, string> = {
  ocrToContext: IMAGE_CONTEXT_MODE_LABELS[0],
  descriptionToContext: IMAGE_CONTEXT_MODE_LABELS[1],
  ignoreImage: IMAGE_CONTEXT_MODE_LABELS[2],
  visionOnlyNoCompaction: IMAGE_CONTEXT_MODE_LABELS[3],
};

/**
 * WHY: API may emit ImageContextMode as a camelCase string or as an int enum.
 * A string-key lookup on a numeric payload yields blank UI.
 */
function formatImageContextMode(mode: AgentImageContextMode | number | string | null | undefined): string {
  if (typeof mode === 'number' && mode >= 0 && mode < IMAGE_CONTEXT_MODE_LABELS.length) {
    return IMAGE_CONTEXT_MODE_LABELS[mode];
  }
  if (typeof mode === 'string' && mode.length > 0) {
    const camel = mode.charAt(0).toLowerCase() + mode.slice(1);
    return IMAGE_CONTEXT_MODE_BY_NAME[mode] ?? IMAGE_CONTEXT_MODE_BY_NAME[camel] ?? mode;
  }
  return IMAGE_CONTEXT_MODE_LABELS[0];
}

/** Parses an agent's speechConfigJson, returning null when absent or malformed. */
const parseSpeechConfig = (json?: string): AgentSpeechConfig | null => {
  if (!json) return null;
  try {
    return JSON.parse(json) as AgentSpeechConfig;
  } catch {
    return null;
  }
};

// ─── Reusable detail-section sub-components ──────────────────────────────────

/** Compact section card with title bar and body — replaces heavy FormSection for overview. */
function DetailSection({ title, children, 'data-testid': testId }: {
  title: string;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <div className="agent-detail__section" data-testid={testId}>
      <div className="agent-detail__section-header">
        <h3 className="agent-detail__section-title">{title}</h3>
      </div>
      <div className="agent-detail__section-body">
        {children}
      </div>
    </div>
  );
}

/** Single horizontal label → value row inside a DetailSection. */
function KvRow({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className="agent-detail__kv-row" data-testid={testId}>
      <dt className="agent-detail__kv-label">{label}</dt>
      <dd className="agent-detail__kv-value">{value}</dd>
    </div>
  );
}

// ─── Page component ──────────────────────────────────────────────────────────

/**
 * Agent detail page — PageDetailLayout for header/breadcrumb/tab chrome,
 * custom DetailSection + KvRow for compact, scannable field display.
 */
export const AgentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  useEffect(() => {
    if (id) {
      void loadAgent();
    }
  }, [id]);

  const loadAgent = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getAgent(id);
      setAgent(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load agent');
      addNotification(err.response?.data?.message || 'Failed to load agent', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (agent && !agent.isSystem) {
      navigate(`/agents/${agent.id}/edit`);
    } else {
      addNotification('System agents cannot be edited', 'warning');
    }
  };

  const handleDelete = async () => {
    if (!agent || agent.isSystem) {
      addNotification('System agents cannot be deleted', 'warning');
      return;
    }

    const ok = await confirm({
      title: 'Delete agent',
      message: `Are you sure you want to delete "${agent.name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;

    try {
      await agentService.deleteAgent(agent.id);
      addNotification('Agent deleted successfully', 'success');
      navigate('/agents');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete agent', 'error');
    }
  };

  const handleClone = async () => {
    if (!agent) return;

    try {
      const cloned = await agentService.cloneAgent(agent.id);
      addNotification('Agent cloned successfully', 'success');
      navigate(`/agents/${cloned.id}`);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to clone agent', 'error');
    }
  };

  const handleParameterAdd = async (_param: CreateAgentParameterRequest) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  const handleParameterUpdate = async (_paramId: string, _param: UpdateAgentParameterRequest) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  const handleParameterDelete = async (_paramId: string) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  const speech = agent ? parseSpeechConfig(agent.speechConfigJson) : null;

  // ─── Overview tab ──────────────────────────────────────────────────────────

  const overviewPanel = agent ? (
    <>
      <DetailSection title="Description" data-testid="agent-section-description">
        <p className="agent-detail__description">
          {agent.description || 'No description available'}
        </p>
      </DetailSection>

      <DetailSection title="Details" data-testid="agent-section-details">
        <dl className="agent-detail__kv-list">
          <KvRow label="Slug" value={agent.slug} testId="agent-detail-slug" />
          <KvRow label="Category" value={agent.category || 'Uncategorized'} testId="agent-detail-category" />
          <KvRow label="Version" value={agent.version} testId="agent-detail-version" />
          <KvRow
            label="Include all default tools"
            value={agent.includeAllDefaultTools ? 'Yes' : 'No'}
            testId="agent-detail-default-tools"
          />
          <KvRow
            label="Agent Framework harness"
            value={
              agent.useHarnessAgent
                ? 'Enabled (execute/plan/auto modes, durable todos, session memory)'
                : 'Disabled (classic chat completions loop)'
            }
            testId="agent-detail-harness"
          />
          <KvRow
            label="Image context"
            value={formatImageContextMode(agent.imageContextMode)}
            testId="agent-detail-image-context"
          />
          <KvRow
            label="Context limit (messages)"
            value={agent.contextLimitLastNMessages != null ? agent.contextLimitLastNMessages : 'No limit'}
            testId="agent-detail-limit-messages"
          />
          <KvRow
            label="Context limit (days)"
            value={agent.contextLimitLastNDays != null ? agent.contextLimitLastNDays : 'No limit'}
            testId="agent-detail-limit-days"
          />
        </dl>
      </DetailSection>
    </>
  ) : null;

  // ─── Configuration tab ─────────────────────────────────────────────────────

  const configurationPanel = agent ? (
    <>
      <FormSection title="System Prompt" data-testid="agent-section-system-prompt">
        <CodeEditor value={agent.systemPrompt} language="plaintext" height="300px" readOnly={true} />
      </FormSection>

      {agent.outputSchemaJson && agent.outputSchemaJson.trim() !== '{}' && (
        <FormSection
          title="Output Schema"
          description="Final responses are returned as JSON matching this schema."
          data-testid="agent-section-output-schema"
        >
          <CodeEditor value={agent.outputSchemaJson} language="json" height="220px" readOnly={true} />
        </FormSection>
      )}

      {speech && (
        <DetailSection title="Speech" data-testid="agent-section-speech">
          <dl className="agent-detail__kv-list">
            <KvRow label="Language" value={speech.language || 'Default (en-US)'} />
            <KvRow
              label="Diarization"
              value={
                (speech.diarization ?? true)
                  ? `Enabled${(speech.diarizeIntermediateResults ?? true) ? ' (including intermediate results)' : ''}`
                  : 'Disabled'
              }
            />
            <KvRow label="Max speakers" value={speech.maxSpeakers != null ? speech.maxSpeakers : 'Default (10)'} />
            <KvRow
              label="Phrase list weight"
              value={speech.phraseList?.weight != null ? speech.phraseList.weight : 'Default (1.0)'}
            />
          </dl>
          {(speech.phraseList?.phrases?.length ?? 0) > 0 && (
            <div className="agent-detail__phrases">
              <p className="agent-detail__phrases-label">Phrases</p>
              <div className="agent-detail__phrases-list">
                {speech.phraseList!.phrases.map((phrase) => (
                  <Chip key={phrase} label={phrase} />
                ))}
              </div>
            </div>
          )}
        </DetailSection>
      )}

      {agent.isSystem && (
        <InlineErrorPanel
          title="System agent"
          message="System agents cannot be edited. Clone this agent to create a customizable version."
          data-testid="agent-error-panel"
        />
      )}
    </>
  ) : null;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="agent-detail">
      <PageDetailLayout
        data-testid="agent-form-page"
        title={agent?.name || 'Agent'}
        subtitle="View and configure agent details."
        breadcrumbs={[{ label: 'Agents', href: '/agents' }, { label: agent?.name || 'Agent' }]}
        backTo={{ label: 'Agents', href: '/agents' }}
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        status={
          agent
            ? { label: agent.isActive ? 'Active' : 'Inactive', color: agent.isActive ? 'success' : 'error' }
            : undefined
        }
        chips={
          agent
            ? [
                ...(agent.category ? [{ label: agent.category, variant: 'outlined' as const }] : []),
                { label: agent.isSystem ? 'System' : 'Custom', variant: 'outlined' as const },
                { label: `v${agent.version}`, variant: 'outlined' as const },
              ]
            : []
        }
        actions={
          agent
            ? [
                {
                  icon: <CloneIcon size={16} />,
                  label: 'Clone',
                  onClick: () => void handleClone(),
                  variant: 'outlined' as const,
                },
                ...(!agent.isSystem
                  ? [
                      {
                        icon: <EditIcon size={16} />,
                        label: 'Edit',
                        onClick: handleEdit,
                        variant: 'outlined' as const,
                      },
                      {
                        icon: <DeleteIcon size={16} />,
                        label: 'Delete',
                        onClick: () => void handleDelete(),
                        variant: 'contained' as const,
                        color: 'error' as const,
                      },
                    ]
                  : []),
              ]
            : []
        }
        defaultTab="overview"
        tabs={
          agent
            ? [
                { label: 'Overview', value: 'overview', content: overviewPanel },
                { label: 'Configuration', value: 'configuration', content: configurationPanel },
                {
                  label: 'Context Window',
                  value: 'context-window',
                  content: <AgentContextWindowPanel agent={agent} readOnly={agent.isSystem} />,
                },
                {
                  label: 'Parameters',
                  value: 'parameters',
                  content: (
                    <ParameterList
                      parameters={agent.parameters}
                      onAdd={handleParameterAdd}
                      onUpdate={handleParameterUpdate}
                      onDelete={handleParameterDelete}
                      readOnly={agent.isSystem}
                    />
                  ),
                },
                {
                  label: 'Run',
                  value: 'run',
                  content: <AgentRunBench agent={agent} />,
                },
                {
                  label: 'File Search',
                  value: 'file-search',
                  content: <AgentMarkdownFileList agentId={agent.id} readOnly={agent.isSystem} />,
                },
                {
                  label: 'Knowledge Files',
                  value: 'knowledge-files',
                  content: <AgentKnowledgeFileList agentId={agent.id} readOnly={agent.isSystem} />,
                },
                ...(agent.defaultModelProviderSupportsHostedSkills
                  ? [
                      {
                        label: 'Skills',
                        value: 'skills',
                        content: (
                          <AgentSkillList
                            agentId={agent.id}
                            readOnly={agent.isSystem}
                            providerId={agent.defaultModelProviderId}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  label: 'Tools',
                  value: 'functions',
                  content: (
                    <AgentDefinitionFunctionList
                      agentId={agent.id}
                      readOnly={agent.isSystem}
                      initialFunctions={agent.functions}
                    />
                  ),
                },
                {
                  label: 'MCP Servers',
                  value: 'mcp-servers',
                  content: <AgentMcpServerList agentId={agent.id} readOnly={agent.isSystem} />,
                },
                {
                  label: 'Sub-Agents',
                  value: 'sub-agents',
                  content: <AttachedAgentsPanel parentAgentId={agent.id} />,
                },
              ]
            : undefined
        }
      />
    </div>
  );
};
